﻿/*
 * Purpose: Presentational input fields for AuthForm (ID, name, and age).
 * Boundary: Pure UI; fully controlled by parent via props; no local state.
 * Invariants:
 * - ASCII only; all text and placeholders use standard ASCII.
 * - No phone number field; handled separately by PhoneNumber component.
 */

import { useState } from "react";

// Component IdField:
// Controlled input for South African ID number
export function IdField({ value, onChange, id }) {
    // Rendering - numeric-only input with 13 digit limit
    return (
        <label className="grid gap-1" htmlFor={id}>
            <span className="text-sm text-neutral-300">ID Number</span>
            <input
                id={id}
                inputMode="numeric"
                maxLength={13}
                autoComplete="username"
                className="bg-neutral-900 rounded-lg px-1 py-2 md:px-3 md:py-2 ring-1 ring-neutral-800 focus:ring-emerald-600 outline-none"
                value={value}
                onChange={onChange}
                placeholder="13 digits"
                required
            />
        </label>
    );
}

// Component NameField:
// Controlled text input with dynamic label
export function NameField({ id, label, value, onChange, autoComplete }) {
    // Rendering - text field labeled via prop
    return (
        <label className="grid gap-1" htmlFor={id}>
            <span className="text-sm text-neutral-300">{label}</span>
            <input
                id={id}
                className="bg-neutral-900 rounded-lg px-1 py-2 md:px-3 md:py-2 ring-1 ring-neutral-800 focus:ring-emerald-600 outline-none"
                value={value}
                onChange={onChange}
                autoComplete={autoComplete}
                required
            />
        </label>
    );
}

// Component AgeField:
// Read-only numeric display showing calculated age
export function AgeField({ value }) {
    // Rendering - locked field reflecting derived age
    return (
        <label className="grid gap-1">
            <span className="text-sm text-neutral-300">Age</span>
            <input
                type="text"
                className="bg-neutral-900 rounded-lg px-3 py-2 ring-1 ring-neutral-800 outline-none cursor-not-allowed"
                value={value ?? ""}
                placeholder="Enter ID number..."
                readOnly
            />
        </label>
    );
}

// Component LoginPassword:
// Controlled password input with Show|Copy buttons matching CreatePassword style
export function LoginPassword({ id, label, value, onChange, autoComplete }) {
    const [showPassword, setShowPassword] = useState(false);

    const onCopy = () => {
        if (value) navigator.clipboard.writeText(value);
    };

    return (
        <div>
            <label className="grid gap-1" htmlFor={id}>
                <span className="text-sm text-neutral-300">{label}</span>
                <input
                    id={id}
                    type={showPassword ? "text" : "password"}
                    className="bg-neutral-900 rounded-lg px-1 py-2 md:px-3 md:py-2 ring-1 ring-neutral-800 focus:ring-emerald-600 outline-none w-full"
                    value={value}
                    onChange={onChange}
                    autoComplete={autoComplete}
                    required
                />
            </label>
            <div className="flex gap-2 mt-2">
                <div className="flex rounded-lg bg-neutral-900 ring-1 ring-neutral-800 overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        className="px-4 py-1 md:px-6 md:py-1 text-white text-xs md:text-base hover:bg-neutral-800 transition"
                    >
                        {showPassword ? "Hide" : "Show"}
                    </button>
                    <div className="w-px bg-neutral-700" />
                    <button
                        type="button"
                        onClick={onCopy}
                        className="px-4 py-1 md:px-6 md:py-1 text-white text-xs md:text-base hover:bg-neutral-800 transition"
                    >
                        Copy
                    </button>
                </div>
            </div>
        </div>
    );
}
